/**
 * What has actually happened across an adviser's clients, newest first.
 *
 * The portal's sidebar had an "Activity Feed" item that opened the Dashboard — a link to a screen
 * that didn't exist. This builds the real thing out of data the client list already carries, so it
 * costs no extra requests: every event below is a timestamp the server derives from unencrypted
 * metadata (invites, saved models, SOAs, fact-find completion).
 *
 * KNOWN LIMITATION, deliberately visible rather than hidden: the client list carries only the LATEST
 * timestamp of each kind per client (`latestSoaAt`, `latestModelAt`, …), not a full history. So a
 * client who has had three SOAs generated contributes one "SOA generated" event, not three. That is
 * honest for "what needs my attention lately", which is what this screen is for; it is NOT an audit
 * trail. The real audit trail is the server's hash-chained audit_events table.
 */
import type { PartnerCase } from './PartnerPortal.tsx';

export type ActivityKind =
    | 'invite_sent'
    | 'client_started'
    | 'profile_completed'
    | 'modelling_saved'
    | 'soa_generated'
    | 'soa_published';

export interface ActivityEvent {
    caseId: string;
    clientName: string;
    kind: ActivityKind;
    at: string;
    /** Whose action this was — the adviser's own, or something the client did. */
    actor: 'client' | 'firm';
    label: string;
}

/** Presentation for each kind: what it says, and a dot colour matching the pipeline's palette. */
export const ACTIVITY_PRESENTATION: Record<ActivityKind, { label: string; actor: 'client' | 'firm'; colour: string }> = {
    invite_sent: { label: 'Invite sent', actor: 'firm', colour: '#3b82f6' },
    client_started: { label: 'Client opened their invite', actor: 'client', colour: '#f59e0b' },
    profile_completed: { label: 'Profile completed', actor: 'client', colour: '#10b981' },
    modelling_saved: { label: 'Modelling saved', actor: 'firm', colour: '#8b5cf6' },
    soa_generated: { label: 'SOA generated', actor: 'firm', colour: '#6366f1' },
    soa_published: { label: 'SOA sent to client', actor: 'firm', colour: '#059669' },
};

/** The workflow timestamps a case can contribute, in the order they'd naturally occur. */
const SOURCES: { kind: ActivityKind; pick: (c: PartnerCase) => string | null | undefined }[] = [
    { kind: 'invite_sent', pick: (c) => c.workflow?.inviteSentAt },
    { kind: 'client_started', pick: (c) => c.workflow?.inviteAcceptedAt },
    { kind: 'profile_completed', pick: (c) => c.workflow?.completedAt },
    { kind: 'modelling_saved', pick: (c) => c.workflow?.latestModelAt },
    { kind: 'soa_generated', pick: (c) => c.workflow?.latestSoaAt },
    { kind: 'soa_published', pick: (c) => c.workflow?.latestPublishedAt },
];

/**
 * @param cases  The adviser's clients, already scoped to what they're allowed to see.
 * @param limit  How many events to return. The feed is a glance, not an archive.
 */
export function buildActivityFeed(cases: PartnerCase[], limit = 50): ActivityEvent[] {
    const events: ActivityEvent[] = [];
    for (const c of cases) {
        for (const source of SOURCES) {
            const at = source.pick(c);
            if (!at) continue;
            const presentation = ACTIVITY_PRESENTATION[source.kind];
            events.push({
                caseId: c.id,
                clientName: c.clientName,
                kind: source.kind,
                at,
                actor: presentation.actor,
                label: presentation.label,
            });
        }
    }
    return events
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .slice(0, limit);
}
